import React, { useEffect, useState, useRef } from 'react';
import { AssessmentState } from '../types';
import { assessmentData } from '../data/criteria';
import { motion } from 'framer-motion';
import { ChevronLeft, Award, Check, Loader2, AlertCircle } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../supabase';
interface ResultsViewProps {
  state: AssessmentState;
  onBack: () => void;
}
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'disabled';
export function ResultsView({
  state,
  onBack
}: ResultsViewProps) {
  // Calculate scores
  let totalNilai = 0;
  const results = assessmentData.map(component => {
    const criteriaScores = component.criteria.map(c => state.scores[c.id] || 0);
    const sum = criteriaScores.reduce((a, b) => a + b, 0);
    const average = sum / criteriaScores.length;
    const nilai = average * component.weight;
    totalNilai += nilai;
    return {
      title: component.title,
      weight: component.weight,
      averageScore: average,
      nilai: nilai
    };
  });
  const getPredicate = (score: number) => {
    if (score >= 3.5) return 'Sangat Baik';
    if (score >= 2.5) return 'Baik';
    if (score >= 1.5) return 'Cukup';
    return 'Kurang';
  };
  const predicate = getPredicate(totalNilai);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const hasSaved = useRef(false);
  useEffect(() => {
    // Hanya simpan sekali per tampilan hasil
    if (hasSaved.current) return;
    hasSaved.current = true;
    if (!isSupabaseConfigured) {
      setSaveStatus('disabled');
      return;
    }
    const saveResult = async () => {
      setSaveStatus('saving');
      const {
        error
      } = await supabase.from('assessment_results').insert({
        penilai: state.penilai,
        student: state.student,
        total_nilai: Number(totalNilai.toFixed(2)),
        predikat: predicate,
        komponen_1: Number((results[0]?.nilai ?? 0).toFixed(2)),
        komponen_2: Number((results[1]?.nilai ?? 0).toFixed(2)),
        komponen_3: Number((results[2]?.nilai ?? 0).toFixed(2)),
        komponen_4: Number((results[3]?.nilai ?? 0).toFixed(2)),
        detail_hasil: results.map(r => ({
          komponen: r.title,
          bobot: r.weight,
          skor_rata_rata: Number(r.averageScore.toFixed(2)),
          nilai: Number(r.nilai.toFixed(2))
        }))
      });
      if (error) {
        console.error('Gagal menyimpan hasil:', error.message);
        setSaveStatus('error');
      } else {
        setSaveStatus('saved');
      }
    };
    saveResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const SaveBadge = () => {
    if (saveStatus === 'saving') {
      return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#86868b]" data-id="element-67">
          <Loader2 className="w-4 h-4 animate-spin" data-id="element-68" />
          Menyimpan hasil…
        </span>;
    }
    if (saveStatus === 'saved') {
      return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-600" data-id="element-69">
          <Check className="w-4 h-4" data-id="element-70" />
          Hasil tersimpan
        </span>;
    }
    if (saveStatus === 'error') {
      return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600" data-id="element-71">
          <AlertCircle className="w-4 h-4" data-id="element-72" />
          Gagal menyimpan ke database
        </span>;
    }
    if (saveStatus === 'disabled') {
      return <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#86868b]" data-id="element-73">
          <AlertCircle className="w-4 h-4" data-id="element-74" />
          Supabase belum dikonfigurasi
        </span>;
    }
    return null;
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.98
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 1.02
  }} transition={{
    duration: 0.5,
    ease: [0.23, 1, 0.32, 1]
  }} className="min-h-screen bg-[#f5f5f7] py-12 px-4 sm:px-6" data-id="element-75">
      <div className="max-w-4xl mx-auto" data-id="element-76">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-8" data-id="element-77">
          <button onClick={onBack} className="flex items-center text-[#0071e3] hover:text-[#0077ed] font-medium text-[17px] transition-colors group" data-id="element-78">
            <ChevronLeft className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform" data-id="element-79" />
            Kembali untuk Edit
          </button>
          <SaveBadge data-id="element-80" />
        </div>

        {/* Header Card */}
        <div className="bg-white rounded-3xl p-8 md:p-10 apple-shadow mb-8 text-center relative overflow-hidden" data-id="element-81">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-[#0071e3]" data-id="element-82" />
          <Award className="w-16 h-16 text-[#0071e3] mx-auto mb-6" data-id="element-83" />
          <h1 className="text-3xl md:text-4xl font-bold text-[#1d1d1f] mb-2 tracking-tight" data-id="element-84">
            Hasil Penilaian
          </h1>
          <p className="text-lg text-[#86868b] mb-8" data-id="element-85">
            Produk Inovasi Bimbingan dan Konseling
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-2xl mx-auto bg-[#f5f5f7] rounded-2xl p-6" data-id="element-86">
            <div data-id="element-87">
              <p className="text-sm font-medium text-[#86868b] mb-1" data-id="element-88">Penilai</p>
              <p className="text-[17px] font-semibold text-[#1d1d1f]" data-id="element-89">
                {state.penilai}
              </p>
            </div>
            <div data-id="element-90">
              <p className="text-sm font-medium text-[#86868b] mb-1" data-id="element-91">
                Produk Milik
              </p>
              <p className="text-[17px] font-semibold text-[#1d1d1f]" data-id="element-92">
                {state.student}
              </p>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white rounded-3xl apple-shadow overflow-hidden mb-8" data-id="element-93">
          <div className="overflow-x-auto" data-id="element-94">
            <table className="w-full text-left border-collapse" data-id="element-95">
              <thead data-id="element-96">
                <tr className="bg-[#f5f5f7] border-b border-[#d2d2d7]" data-id="element-97">
                  <th className="py-4 px-6 text-sm font-semibold text-[#1d1d1f]/70 w-1/2" data-id="element-98">
                    Komponen
                  </th>
                  <th className="py-4 px-6 text-sm font-semibold text-[#1d1d1f]/70 text-center" data-id="element-99">
                    Bobot
                  </th>
                  <th className="py-4 px-6 text-sm font-semibold text-[#1d1d1f]/70 text-center" data-id="element-100">
                    Skor Rata-rata
                    <br data-id="element-101" />
                    <span className="text-xs font-normal" data-id="element-102">(1-4)</span>
                  </th>
                  <th className="py-4 px-6 text-sm font-semibold text-[#1d1d1f]/70 text-right" data-id="element-103">
                    Nilai
                    <br data-id="element-104" />
                    <span className="text-xs font-normal" data-id="element-105">(Skor × Bobot)</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#d2d2d7]/50" data-id="element-106">
                {results.map((res, idx) => <tr key={idx} className="hover:bg-[#f5f5f7]/50 transition-colors" data-id="element-107">
                    <td className="py-5 px-6 text-[15px] font-medium text-[#1d1d1f] leading-snug" data-id="element-108">
                      {res.title}
                    </td>
                    <td className="py-5 px-6 text-[15px] text-[#86868b] text-center" data-id="element-109">
                      {res.weight * 100}%
                    </td>
                    <td className="py-5 px-6 text-[15px] font-semibold text-[#1d1d1f] text-center" data-id="element-110">
                      {res.averageScore.toFixed(2)}
                    </td>
                    <td className="py-5 px-6 text-[15px] font-semibold text-[#0071e3] text-right" data-id="element-111">
                      {res.nilai.toFixed(2)}
                    </td>
                  </tr>)}
              </tbody>
              <tfoot className="bg-[#f5f5f7] border-t-2 border-[#d2d2d7]" data-id="element-112">
                <tr data-id="element-113">
                  <td className="py-5 px-6 text-[15px] font-bold text-[#1d1d1f]" data-id="element-114">
                    Nilai Total
                  </td>
                  <td className="py-5 px-6 text-[15px] font-bold text-[#1d1d1f] text-center" data-id="element-115">
                    100%
                  </td>
                  <td className="py-5 px-6 text-[15px] font-bold text-[#1d1d1f] text-center" data-id="element-116">
                    -
                  </td>
                  <td className="py-5 px-6 text-[17px] font-bold text-[#0071e3] text-right" data-id="element-117">
                    {totalNilai.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Final Score Hero */}
        <div className="bg-white rounded-3xl p-10 apple-shadow text-center" data-id="element-118">
          <p className="text-lg font-medium text-[#86868b] mb-2 uppercase tracking-wider" data-id="element-119">
            Nilai Akhir
          </p>
          <div className="text-7xl md:text-8xl font-bold text-[#1d1d1f] tracking-tighter mb-4" data-id="element-120">
            {totalNilai.toFixed(2)}
          </div>
          <div className={`inline-block px-6 py-2 rounded-full text-lg font-semibold ${totalNilai >= 3.5 ? 'bg-green-100 text-green-800' : totalNilai >= 2.5 ? 'bg-blue-100 text-blue-800' : totalNilai >= 1.5 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-121">
            {predicate}
          </div>
          <p className="text-sm text-[#86868b] mt-6" data-id="element-122">
            Σ (Skor Rata-rata Komponen × Bobot)
          </p>
        </div>
      </div>
    </motion.div>;
}